import React from "react";
import { IModel, BaseModel } from "../../../../core/util/BaseModel";
import { AppService } from "strikejs-app-service";
import {
  IInnerNavContentConfig,
  IInnerNavContentModel,
  InnerNavContentModel
} from "../../../../components/widgets/innerNavigation/InnerNavContent_model";
import { ProjectSettings } from "./ProjectSettings_view";
import _ from "lodash";
import { observable, action } from "mobx";
import { ProjectSettingsModel } from "./ProjectSettings_model";
import { MilestoneSettingsView } from "./milestones/MilestoneSettings_view";
import { ProjectPermission } from "./permissions/ProjectPermissions_view";
import { ProjectPermissionModel } from "./permissions/ProjectPermissions_model";
import { RLink } from "../../../../components/ui/Rlink";
import { IProjectsApi } from "../../../../services/api/v1/projects/IProject.api";
import { Services } from "../../../../constants";
import I18n from "../../../../core/localization/I18n";
import { CustomProperties } from "./customProperties/CustomProperties_view";
import { IOrganisationSettingsService } from "../../../../services/local/settingsService/IOrganisationSettingsService";

export interface ISettingsViewModel extends IModel {
  innerNavContentModel: IInnerNavContentModel;
  isLoading: boolean;
}

export class SettingsViewModel extends BaseModel implements ISettingsViewModel {
  innerNavContentConfig: IInnerNavContentConfig;
  innerNavContentModel: IInnerNavContentModel;
  @observable isLoading: boolean = true;
  projectSettings: ProjectSettingsModel;
  permissionSettings: ProjectPermissionModel;
  projectId: number;
  project: FP.Entities.IProject;
  projectProvider: IProjectsApi;
  organisationSettings: IOrganisationSettingsService;
  appService: AppService;
  authUser: FP.Entities.IUser;
  refreshClaims: () => void;
  page?: string;
  organisationId: number;
  isAdmin: boolean;
  /**
   *
   */
  constructor(
    appService: AppService,
    organisationId: number,
    projectId: number,
    authUser: FP.Entities.IUser,
    refreshClaims: () => void,
    isAdmin: boolean,
    page?: string
  ) {
    super();
    this.refreshClaims = refreshClaims;
    this.isAdmin = isAdmin;
    this.projectId = projectId;
    this.appService = appService;
    this.organisationId = organisationId;
    this.authUser = authUser;
    this.page = page;
    this.projectProvider = appService.getService<IProjectsApi>(Services.ProjectsApi);
    this.organisationSettings = appService.getService<IOrganisationSettingsService>(Services.OrganisationSettingsService);
    this.loadProject(this.projectId);
  }

  showCustomProperies = async () =>
    this.isAdmin && await this.organisationSettings.isCustomPropertiesEnabled(this.organisationId);

  @action
  loadProject = async (id: number) => {
    let res = await this.projectProvider.getById(this.organisationId, id);

    if (!res || res.isError) {
      return;
    }
    this.project = res.payload;

    this.projectSettings = new ProjectSettingsModel(this.appService, id, this.authUser, this.refreshClaims);
    this.permissionSettings = new ProjectPermissionModel(this.appService);
    this.projectSettings.setProject(this.project);
    this.permissionSettings.setProject(this.project);
    let contentItems = {
      project: {
        key: "project",
        label: "Project",
        content: <ProjectSettings model={this.projectSettings} />
      },
      permissions: {
        key: "permissions",
        label: "Permissions",
        content: <ProjectPermission model={this.permissionSettings} />
      },
      milestones: {
        key: "milestones",
        label: "Milestones",
        content: <MilestoneSettingsView project={this.project} />
      }
    };

    if (await this.showCustomProperies()) {
      contentItems["customProperties"] = {
        key: "customProperties",
        label: I18n.t("entities.customProperties"),
        content: <CustomProperties />
      }
    }
    this.innerNavContentConfig = {
      items: contentItems,
      navigationTitle: "Settings",
      displayNavWithLinks: true,
      linkRender: e => (
        <RLink to={`/organisations/${this.organisationId}/projects/${this.projectId}/settings/${e.key}`}>
          <h4 id={`inner-nav-${e.key}`} className="mb-0">{e.label}</h4>
        </RLink>
      )
    };

    if (!this.project.isPrivate) {
      delete this.innerNavContentConfig.items.permissions;
    }

    this.innerNavContentModel = new InnerNavContentModel(this.innerNavContentConfig);
    const page = this.page;
    const keys = _.map(this.innerNavContentConfig.items, e => e.key);
    if (page && keys.indexOf(page) >= 0) {
      this.innerNavContentModel.setCurrentViewKey(page);
    }
    this.isLoading = false;
  };
}
